import { useMainStore } from "@/stores/mainStore";
import { listModule, phase } from "@/types/global";
import { useTranslation } from "i18next-vue";
import { computed, ComputedRef, onMounted, Ref, ref, toRaw } from "vue";
import axios from "axios";
import { useSnackbar } from "vuetify-use-dialog";

/**
 * Costs of a phase (module -> slots) for a payment mode in euro
 * @param phaseSlots slots per module, keyed by `key`
 * @param key which module property is used as key (name or value)
 */
export function getPhaseCosts(
  phaseSlots: Record<string | number, number> | undefined,
  mode: string | undefined,
  modules: { name: string; value: number; price: number; price_yearly: number }[],
  key: "name" | "value" = "value"
) {
  if (!phaseSlots) return 0;
  return (
    modules.reduce((acc, module) => {
      const price = mode === "yearly" ? module.price_yearly : module.price;
      return acc + (phaseSlots[module[key]] || 0) * (price || 0);
    }, 0) / 100
  );
}

/**
 * Slots that cost money (keys are module values): calc_1 is free, private_spaces
 * alone is the mandatory slot of an organization that is waived while only module
 * trials are used (same rule as hasRealBooking on the server)
 */
export function hasRealBooking(slots: Record<string | number, number>) {
  return Object.entries(slots).some(
    ([key, value]) => Number(key) !== 1 && Number(key) !== 32 && value > 0
  );
}

export function useModules(
  slots: Ref<{} | undefined>,
  allowedEdit: boolean,
  current_status: Ref<String | undefined>,
  licenceId: Ref<number | undefined>,
  paymentMode: Ref<string | undefined>,
  current_phase: Ref<phase | undefined>,
  next_phase: Ref<phase | undefined>,
  current_phase_mode: string = "monthly",
  next_billing_cycle_anchor: number | null,
  next_phase_mode: string = "monthly",
  promoCode: Ref<string | undefined> = ref(undefined),
  promoInfo: Ref<PromoInfo | null | undefined> = ref(undefined),
  activeDiscount: Ref<PromoInfo | null | undefined> = ref(undefined),
  forOrganization = false
) {
  const { t } = useTranslation();
  const mainStore = useMainStore();
  // const status_by_module = computed(() => {
  //   const result = {};
  //   for (const [type, modules] of Object.entries(current_status.value)) {
  //     for (const m of modules as string[]) {
  //       result[m] = type;
  //     }
  //   }
  // });
  //
  const someoneLeft = ref(false);

  onMounted(() => {
    console.log("useModules next_phase", next_phase.value);

    for (const module of mainStore.modules) {
      if (module.disabled) continue;
      if (allowedEdit && module.value === 1 << 5) continue;
      console.log("Im here,", next_phase.value[module.value]);

      if (next_phase.value[module.value] !== slots.value[module.value]) {
        someoneLeft.value = true;
      }

      const element: listModule = Object.assign({}, module, {
        slots:
          current_status.value !== null ? next_phase.value[module.value] : 0,
        monthEnd: Math.max(
          0,
          current_phase.value[module.value] - next_phase.value[module.value]
        )
      });

      if (slots.value[module.value] === undefined) {
        return [];
      }

      initialItems.value.push(element);
    }
  });

  const headers: any = computed(() => {
    return [
      {
        title: t("views.licence.table.module"),
        align: "start",
        sortable: false,
        key: "module",
        value: (item) => t("views.licence.modules." + item.name + ".name"),
        width: "25%"
      },
      {
        title: t("views.licence.table.description"),
        align: "start",
        sortable: false,
        key: "description",
        value: (item) =>
          t("views.licence.modules." + item.name + ".description"),
        width: "25%"
      },
      {
        title: t("views.licence.table.active"),
        key: "active",
        sortable: false,
        width: "25%",
        hidden: !allowedEdit
      },
      {
        title: t("views.licence.table.slots"),
        key: "slots",
        value: (item) => item.slots,
        width: "25%",
        hidden: allowedEdit
      },
      {
        title: t("views.licence.table.price"),
        key: "price",
        sortable: false,
        align: "end",
        value: (item) => {
          console.log(
            item.module,
            item.price,
            Math.floor(item.price) / 100 + " €"
          );
          return (
            Math.floor(
              paymentMode.value === "yearly" ? item.price_yearly : item.price
            ) /
              100 +
            " €"
          );
        },
        width: "25%"
      },
      {
        title: t("views.licence.table.total"),
        key: "priceAll",
        sortable: false,
        align: "end",
        value: (item) => {
          console.log(
            "paymentMode",
            paymentMode.value,
            item.module,
            item.price,
            item.price_yearly,
            Math.floor(item.price) / 100 + " €"
          );
          return (
            Math.floor(
              (paymentMode.value === "yearly"
                ? item.price_yearly
                : item.price) * item.slots
            ) /
              100 +
            " €"
          );
        },
        width: "25%"
      }
    ].filter((i) => !i.hidden);
  });

  const initialItems: Ref<listModule[]> = ref([]);

  const items: ComputedRef<listModule[]> = computed(() => {
    const result = [];
    for (const module of mainStore.modules) {
      if (module.disabled) continue;
      if (allowedEdit && module.value === 1 << 5) continue;

      const element: listModule = Object.assign({}, module, {
        slots: slots.value[module.value],
        monthEnd: Math.max(
          0,
          next_phase.value[module.value] - slots.value[module.value]
        )
        // payment_state: status_by_module.value[module.value]
      });

      result.push(element);
    }

    return result;
  });

  const monthlyToYearly = computed(() => {
    return paymentMode.value === "yearly" && current_phase_mode === "monthly";
  });

  const yearlyToMonthly = computed(() => {
    return paymentMode.value === "monthly" && current_phase_mode === "yearly";
  });

  const oldMonthlyCost = computed(() => {
    return (
      initialItems.value.reduce((acc, module) => {
        acc += module.price * module.slots;
        return acc;
      }, 0) / 100
    );
  });

  const oldYearlyCost = computed(() => {
    return (
      initialItems.value.reduce((acc, module) => {
        acc += module.price_yearly * module.slots;
        return acc;
      }, 0) / 100
    );
  });

  const monthlyCost = computed(() => {
    return (
      items.value.reduce((acc, module) => {
        acc += module.price * module.slots;
        return acc;
      }, 0) / 100
    );
  });

  const yearlyCost = computed(() => {
    return (
      items.value.reduce((acc, module) => {
        acc += module.price_yearly * module.slots;
        return acc;
      }, 0) / 100
    );
  });

  // At most one price discount per licence: a newly applied code, else the active one
  const effectiveDiscount = computed(() => {
    const info = promoInfo.value;
    if (promoCode.value && info?.type === "discount") {
      return { info, code: promoCode.value, active: false };
    }
    const active = activeDiscount.value;
    if (active?.type === "discount") {
      return { info: active, code: active.code ?? "", active: true };
    }
    return null;
  });

  function applyDiscount(costs: number, info: PromoInfo) {
    let result = costs;
    if (info.percent_off) result = costs * (1 - info.percent_off / 100);
    else if (info.amount_off) result = Math.max(0, costs - info.amount_off / 100);
    return Math.round(result * 100) / 100;
  }

  const modulesAdded: ComputedRef<Record<number, number>> = computed(() => {
    console.log("modulesAdded ", toRaw(items.value), toRaw(initialItems.value));
    return Object.fromEntries(
      items.value.map((module, index) => {
        console.log(module, index, toRaw(initialItems.value[index]));
        return [module.value, module.slots - initialItems.value[index].slots];
      })
    );
  });

  // Mirrors MODULES_PAYMENT_MODE_KEEP_CURRENT of the server: the current
  // monthly period runs to its end and yearly starts afterwards
  const keepsCurrentMode = computed(() => {
    if (!monthlyToYearly.value) {
      return false;
    }
    const removedFromCurrent = Object.entries(slots.value ?? {}).some(
      ([name, value]) => (value as number) < (current_phase.value?.[name] ?? 0)
    );
    return removedFromCurrent || next_phase_mode === "yearly";
  });

  const chargeNeeded = computed(() => {
    if (!next_billing_cycle_anchor) {
      return 0;
    }

    if (monthlyToYearly.value && !keepsCurrentMode.value) {
      return 1;
    }
    return Object.entries(modulesAdded.value).reduce((acc, [name, i]) => {
      // slots up to current_phase are already paid until the period end
      return i > 0 && current_phase.value[Number(name)] < slots.value[Number(name)]
        ? acc + i
        : acc;
    }, 0);
  });

  const changeNeeded = computed(() => {
    // an applied code is saved even without a module change
    if (promoCode.value) {
      return true;
    }
    // also covers reverting a pending switch
    if (next_billing_cycle_anchor && paymentMode.value !== next_phase_mode) {
      return true;
    }
    return initialItems.value.reduce((result, initItem, index) => {
      return result || initItem.slots !== items.value[index].slots;
    }, false);
  });

  const modulesRemoved = computed(() => {
    return Object.fromEntries(
      initialItems.value.map((module, index) => [
        module.name,
        module.slots - items.value[index].slots
      ])
    );
  });

  // A module trial is no price: the preview route rejects it and redeems nothing
  const previewCode = computed(() =>
    promoInfo.value?.type === "module_trial" ? undefined : promoCode.value
  );

  // A promo code changes the price although no slot is added
  const previewNeeded = computed(
    () => chargeNeeded.value > 0 || !!previewCode.value
  );

  // "0 € today, from {date} {amount}" of a customer trial / a trialing subscription
  // create: a new customer trial (card needed, ends by itself), else a running one
  const trialInfo = ref<null | { date: number; amount: string; create: boolean }>(null);

  const previewLoading = ref(false);
  const previewFailed = ref(false);
  // the preview has nothing to charge now (amount_due 0), no "0 €" is shown
  const nothingDue = ref(false);
  // answers of an earlier popup opening are dropped
  let previewRequest = 0;

  function loadPreview() {
    const request = ++previewRequest;
    additionalPrice.value = null;
    trialInfo.value = null;
    previewFailed.value = false;
    nothingDue.value = false;
    previewLoading.value = false;
    if (!previewNeeded.value) return;
    previewLoading.value = true;
    const byName = Object.fromEntries(
      mainStore.modules.map((module) => [
        module.name,
        slots.value[module.value]
      ])
    );

    axios
      .post(
        current_status.value !== null
          ? `/api/stripe/preview-sub-update/${licenceId.value}`
          : "/api/stripe/preview-sub-create",
        {
          byName,
          paymentMode: paymentMode.value,
          current_phase_elements: current_phase.value,
          current_phase_mode: current_phase_mode,
          promoCode: previewCode.value || undefined,
          // the code is checked like for an organization (no customer trial)
          forOrganization: forOrganization || undefined
        }
      )
      .then((response) => {
        if (request !== previewRequest) return;
        console.log("Success", response.data);
        nothingDue.value = response.data.amount_due === 0;
        additionalPrice.value =
          response.data.amount_due / 100 +
          " " +
          t("units." + response.data.currency);
        // create preview: `trial`, update preview of a trial subscription: `trialing`
        const trial = response.data.trial;
        if (trial || response.data.trialing) {
          trialInfo.value = {
            create: !!trial,
            date: trial ? trial.trial_end : response.data.trial_end,
            amount:
              (trial ? trial.amount_after : response.data.amount_after) / 100 +
              " " +
              t("units." + response.data.currency)
          };
        }
      })
      .catch((error) => {
        if (request !== previewRequest) return;
        // invalid code etc.: the popup shows a hint, saving stays possible
        console.log("Preview failed", error);
        previewFailed.value = true;
      })
      .finally(() => {
        if (request === previewRequest) previewLoading.value = false;
      });
  }

  const additionalPrice = ref<null | string>(null);

  //   const remainingDaysMonth = computed(() => {
  //     const now = new Date();
  //     const monthDays = new Date(
  //       now.getFullYear(),
  //       now.getMonth() + 1,
  //       0
  //     ).getDate();
  //     const remainingDays = monthDays - now.getDate() + 1;
  //     return remainingDays;
  //   });
  //
  //   const remainingDaysYear = computed(() => {
  //     const now = new Date();
  //     const yearDays = new Date(now.getFullYear() + 1, 0, 1).getTime();
  //     const remainingDays =
  //       Math.round((yearDays - now.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  //     return remainingDays;
  //   });

  const remainingDays = computed(() => {
    const now = new Date().getTime() / 1000;
    if (!next_billing_cycle_anchor) {
      return 0;
    }
    return Math.round((next_billing_cycle_anchor - now) / (60 * 60 * 24)) + 1;
  });

  // A switch of the payment mode was already saved, but the old mode still runs
  const pendingSwitch = computed(() => {
    return (
      !!next_billing_cycle_anchor && current_phase_mode !== next_phase_mode
    );
  });

  const reviewData = computed(() => {
    const listCosts =
      paymentMode.value === "yearly" ? yearlyCost.value : monthlyCost.value;
    const found = effectiveDiscount.value;
    let discount: null | {
      code: string;
      duration: "once" | "repeating" | "forever";
      months: number | null;
      active: boolean;
      listCosts: number;
      discountedCosts: number;
      end: number | null;
    } = null;
    if (found) {
      // yearly payment: only the first yearly invoice falls into the months
      const duration =
        found.info.duration === "repeating" && paymentMode.value === "yearly"
          ? "once"
          : (found.info.duration ?? "once");
      const months = found.info.duration_in_months ?? null;
      let end = found.info.end ?? null;
      if (!found.active && duration === "repeating" && months) {
        const date = new Date();
        date.setMonth(date.getMonth() + months);
        end = Math.floor(date.getTime() / 1000);
      }
      discount = {
        code: found.code,
        duration,
        months,
        active: found.active,
        listCosts,
        discountedCosts: applyDiscount(listCosts, found.info),
        end: duration === "repeating" ? end : null
      };
    }
    return {
      remainingDays: remainingDays.value,
      oldCosts:
        paymentMode.value === "yearly"
          ? oldYearlyCost.value
          : oldMonthlyCost.value,
      newCosts: listCosts,
      discount,
      // module trial code kept until the save redeems it
      moduleTrial:
        promoInfo.value?.type === "module_trial" && promoCode.value
          ? { modules: promoInfo.value.modules ?? {}, days: promoInfo.value.days }
          : null,
      additionalPrice: additionalPrice.value,
      previewLoading: previewLoading.value,
      previewFailed: previewFailed.value,
      // A running subscription is updated and paid right away with the stored
      // method, no next step shows the amount. Without a subscription (no anchor,
      // also a trial path organization) the checkout shows it. A trialing
      // subscription is blocked too, it cannot be told apart here.
      saveBlocked:
        previewFailed.value &&
        current_status.value !== null &&
        !!next_billing_cycle_anchor,
      nothingDue: nothingDue.value,
      trialInfo: trialInfo.value,
      yearly: paymentMode.value === "yearly",
      date: next_billing_cycle_anchor,
      switchYearly: monthlyToYearly.value,
      switchMonthly: yearlyToMonthly.value,
      keepMonthly: keepsCurrentMode.value,
      pendingSwitch: pendingSwitch.value,
      revert: pendingSwitch.value && paymentMode.value === current_phase_mode,
      currentMode: current_phase_mode,
      nextMode: next_phase_mode,
      currentCosts: getPhaseCosts(
        current_phase.value,
        current_phase_mode,
        mainStore.modules
      ),
      nextCosts: getPhaseCosts(
        next_phase.value,
        next_phase_mode,
        mainStore.modules
      )
    };
  });

  return {
    headers,
    initialItems,
    items,
    oldMonthlyCost,
    oldYearlyCost,
    monthlyCost,
    yearlyCost,
    modulesRemoved,
    additionalPrice,
    loadPreview,
    previewNeeded,
    chargeNeeded,
    changeNeeded,
    someoneLeft,
    reviewData
  };
}


export type PromoInfo = {
  type: "discount" | "customer_trial" | "module_trial";
  days?: number;
  modules?: Record<string, number>;
  percent_off?: number | null;
  amount_off?: number | null;
  currency?: string | null;
  duration?: "once" | "repeating" | "forever";
  duration_in_months?: number | null;
  // active discounts only
  code?: string;
  end?: number | null;
};

/** How long an active discount (describeLicencePromotion) is valid, as text */
export function discountValidity(
  t: (key: string, options?: any) => string,
  promotion: PromoInfo
): string {
  const duration = promotion.duration ?? "once";
  if (duration !== "repeating") return t("views.licence.promo.validity." + duration);
  return promotion.end
    ? t("views.licence.promo.validity.repeatingUntil", {
        date: new Date(promotion.end * 1000).toLocaleDateString(),
        interpolation: { escapeValue: false }
      })
    : t("views.licence.promo.validity.repeating", {
        months: promotion.duration_in_months
      });
}

/** Translated, comma separated text for a { module: slots } map (promo and trial texts) */
export function formatModuleSlots(
  t: (key: string, options?: any) => string,
  mods: Record<string, number> = {}
): string {
  return Object.entries(mods)
    .map(([name, q]) =>
      t("views.licence.promo.moduleSlots", {
        module: t("views.licence.modules." + name + ".name"),
        count: q,
        interpolation: { escapeValue: false }
      })
    )
    .join(", ");
}

/**
 * Promo code input (docs: .claude/plans/stripe-discounts-promo-codes.md).
 * - discount / customer_trial: the code is kept and sent as `promoCode` with the
 *   preview and the save requests (appliedCode)
 * - module_trial: kept like the others (appliedCode, keptTrial), the save request
 *   redeems it on the server
 * @param licenceId licence the code is checked for (undefined = private licence)
 * @param newOrganization the organization does not exist yet: the code is checked
 *   for an organization (not against the private licence)
 */
export function usePromo(
  licenceId: Ref<number | undefined>,
  newOrganization = false
) {
  const { t } = useTranslation();
  const mainStore = useMainStore();
  const createSnackbar = useSnackbar();

  const promoCode = ref("");
  const appliedCode = ref<string | undefined>(undefined);
  const promoInfo = ref<PromoInfo | null>(null);
  const promoError = ref<string | null>(null);
  const applying = ref(false);

  const moduleName = (name: string) => t("views.licence.modules." + name + ".name");
  const modulesText = (mods: Record<string, number>) => formatModuleSlots(t, mods);

  /** Translated text for an error response `{ error, data }` of the promo / slot routes */
  function errorText(error: string | undefined, data: any = {}): string {
    const key = "views.licence.promo.errors." + error;
    return t(
      [key, "views.licence.promo.errors.unknown"],
      {
        modules: data?.modules ? data.modules.map(moduleName).join(", ") : "",
        module: data?.module ? moduleName(data.module) : "",
        allowed: data?.allowed,
        assigned: data?.assigned,
        interpolation: { escapeValue: false }
      }
    );
  }

  /** Display text of a promotion (as returned by describePromotion) */
  function describe(info: PromoInfo): string {
    if (info.type === "module_trial") {
      return t("views.licence.promo.moduleTrial", {
        modules: modulesText(info.modules ?? {}),
        days: info.days,
        interpolation: { escapeValue: false }
      });
    }
    if (info.type === "customer_trial") {
      return t("views.licence.promo.customerTrial", { days: info.days, interpolation: { escapeValue: false } });
    }
    const amount = info.percent_off
      ? `${info.percent_off} %`
      : `${(info.amount_off ?? 0) / 100} ${t("units." + info.currency)}`;
    return t("views.licence.promo.discount." + (info.duration ?? "once"), {
      amount,
      months: info.duration_in_months,
      interpolation: { escapeValue: false }
    });
  }

  async function applyPromo() {
    const code = promoCode.value.trim();
    if (!code) return;
    applying.value = true;
    promoError.value = null;
    try {
      const { data } = await axios.post("/api/licence/promo/validate", {
        code,
        licenceId: licenceId.value || undefined,
        forOrganization: newOrganization || undefined
      });
      promoInfo.value = data;
      appliedCode.value = code;
    } catch (error: any) {
      promoInfo.value = null;
      appliedCode.value = undefined;
      promoError.value = errorText(
        error?.response?.data?.error,
        error?.response?.data?.data
      );
    } finally {
      applying.value = false;
    }
  }

  // module trial that is kept until the save redeems it: { module: slots }
  const keptTrial = computed(() =>
    promoInfo.value?.type === "module_trial" && appliedCode.value
      ? (promoInfo.value.modules ?? null)
      : null
  );

  function removePromo() {
    promoCode.value = "";
    appliedCode.value = undefined;
    promoInfo.value = null;
    promoError.value = null;
  }

  /** Shows the `{ error, data }` of a failed request as snackbar, false if there is none */
  function showApiError(error: any): boolean {
    const data = error?.response?.data;
    if (!data?.error) return false;
    createSnackbar({
      text: errorText(data.error, data.data),
      showCloseButton: false,
      snackbarProps: { color: "error", timeout: 4000 }
    });
    return true;
  }

  return {
    promoCode,
    appliedCode,
    promoInfo,
    promoError,
    applying,
    keptTrial,
    applyPromo,
    removePromo,
    describe,
    errorText,
    showApiError,
    modulesText,
    moduleName
  };
}
